import { useEffect, useState } from 'react';
import { userAPI, categoryAPI } from '../api';
import { Save, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const usersRes = await userAPI.getAll();
      if (usersRes.data && usersRes.data.length > 0) {
        setUser(usersRes.data[0]);
      } else {
        setUser({
          name: 'User',
          currency: 'SAR',
          timezone: 'Asia/Riyadh',
          start_of_month: 1,
          salary_day: 1,
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading user:', error);
      toast.error('Failed to load settings');
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (user.id) {
        await userAPI.update(user.id, user);
      } else {
        const res = await userAPI.create(user);
        setUser(res.data);
      }
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const handleSeedCategories = async () => {
    try {
      await categoryAPI.seed();
      toast.success('Default categories created!');
    } catch (error) {
      console.error('Error seeding categories:', error);
      toast.error('Failed to create categories');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-900 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 p-6 md:p-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-green-900 mb-2">Settings</h1>
          <p className="text-stone-500 text-lg">Manage your preferences and data</p>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-stone-200 mb-6" data-testid="user-settings">
          <h2 className="text-xl font-bold font-heading text-green-900 mb-6">User Preferences</h2>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-stone-700 mb-2">Name</label>
              <input
                type="text"
                value={user.name}
                onChange={(e) => setUser({ ...user, name: e.target.value })}
                required
                data-testid="input-name"
                className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">Currency</label>
                <input
                  type="text"
                  value={user.currency}
                  onChange={(e) => setUser({ ...user, currency: e.target.value })}
                  required
                  data-testid="input-currency"
                  className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">Timezone</label>
                <input
                  type="text"
                  value={user.timezone}
                  onChange={(e) => setUser({ ...user, timezone: e.target.value })}
                  required
                  data-testid="input-timezone"
                  className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">Start of Month (Day)</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={user.start_of_month}
                  onChange={(e) => setUser({ ...user, start_of_month: parseInt(e.target.value) })}
                  required
                  data-testid="input-start-month"
                  className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">Salary Day</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={user.salary_day}
                  onChange={(e) => setUser({ ...user, salary_day: parseInt(e.target.value) })}
                  required
                  data-testid="input-salary-day"
                  className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-settings-btn"
              className="w-full bg-green-900 text-white px-6 py-3 rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95 disabled:opacity-50"
            >
              <Save className="inline-block w-5 h-5 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-stone-200" data-testid="data-management">
          <h2 className="text-xl font-bold font-heading text-green-900 mb-6">Data Management</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center pb-4 border-b border-stone-200">
              <div>
                <h3 className="font-bold text-stone-900">Seed Default Categories</h3>
                <p className="text-sm text-stone-600">Create common expense and income categories</p>
              </div>
              <button
                onClick={handleSeedCategories}
                data-testid="seed-categories-btn"
                className="bg-stone-100 text-stone-900 px-6 py-2 rounded-full font-medium hover:bg-stone-200 transition-all"
              >
                Seed
              </button>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-bold text-stone-900">Export Data</h3>
                <p className="text-sm text-stone-600">Download your financial data as CSV</p>
              </div>
              <button
                onClick={() => toast.info('Export feature coming soon')}
                data-testid="export-data-btn"
                className="bg-stone-100 text-stone-900 px-6 py-2 rounded-full font-medium hover:bg-stone-200 transition-all"
              >
                <Download className="inline-block w-5 h-5 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
